import React, { useState, useEffect } from 'react';
import { Stack, Typography, Box, Button, Link } from '@mui/joy';
import { useSession } from 'context/SessionContext';
import { getEmployer } from '../../../../apiconfig/api';

interface SharePositionStepProps {
  data: {
    companyName: string;
    jobTitle: string;
    employerSlug?: string;
    positionSlug?: string;
  };
  onUpdate: (updates: any) => void;
}

const SharePositionStep: React.FC<SharePositionStepProps> = ({ data }) => {
  const { sessionEmployerId } = useSession();
  const [employerData, setEmployerData] = useState<any>(null);
  const [loadingEmployer, setLoadingEmployer] = useState(false);

  // Fetch employer data to get the slug (only if we don't already have the employer slug)
  useEffect(() => {
    const fetchEmployerData = async () => {
      if (sessionEmployerId && !data.employerSlug) {
        try {
          setLoadingEmployer(true);
          const response = await getEmployer(sessionEmployerId);
          setEmployerData(response.data.employer);
        } catch (error) {
          console.error('Error fetching employer data:', error);
          // Don't show error to user, just use fallback values
        } finally {
          setLoadingEmployer(false);
        }
      }
    };

    fetchEmployerData();
  }, [sessionEmployerId, data.employerSlug]);

  // Generate position URL using actual slugs from workflow data or API fallback
  const getPositionUrl = () => {
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const employerSlug = employerData?.slug || data.employerSlug;
    const jobSlug = data.positionSlug;
    return `${baseUrl}/apply/${employerSlug}/${jobSlug}`;
  };

  const positionUrl = getPositionUrl();

  const handleCopyLink = () => {
    navigator.clipboard.writeText(positionUrl);
    // TODO: Show success toast
    console.log('Link copied to clipboard');
  };

  const handleShareEmail = () => {
    const subject = `Job Opening: ${data.jobTitle} at ${data.companyName}`;
    const body = `We're hiring! Check out this ${data.jobTitle} position at ${data.companyName}:\n\n${positionUrl}`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const handleShareSocial = (platform: string) => {
    const text = `We're hiring! ${data.jobTitle} position at ${data.companyName}`;
    const url = positionUrl;
    
    let shareUrl = '';
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  return (
    <Stack spacing={4}>
      {/* Success Message */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography level="h2" sx={{ color: '#2e2d7e', mb: 2, fontSize: '3rem' }}>
          🎉
        </Typography>
        <Typography level="h3" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 2 }}>
          Your job post is now active!
        </Typography>
        <Typography level="body-lg" sx={{ color: '#7B7F85' }}>
          Your {data.jobTitle} post is now active and ready to receive applications.
        </Typography>
      </Box>

      {/* Position Link */}
      <Box sx={{ p: 3, backgroundColor: '#F8F9FA', borderRadius: '12px', border: '2px solid #E5E7EB' }}>
        <Typography level="body-md" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 2 }}>
          Your Job URL:
        </Typography>
        <Box
          sx={{
            p: 2,
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            color: '#2e2d7e',
            wordBreak: 'break-all',
            mb: 2
          }}
        >
          {(loadingEmployer && !data.employerSlug) ? 'Generating URL...' : positionUrl}
        </Box>
        <Button
          variant="outlined"
          onClick={handleCopyLink}
          disabled={loadingEmployer && !data.employerSlug}
          sx={{
            borderColor: '#2e2d7e',
            color: '#2e2d7e',
            '&:hover': {
              borderColor: '#1a1a5e',
              color: '#1a1a5e',
            },
            '&:disabled': {
              borderColor: '#E5E7EB',
              color: '#7B7F85',
            },
          }}
        >
          {(loadingEmployer && !data.employerSlug) ? 'Loading...' : 'Copy Link'}
        </Button>
      </Box>

      {/* Share Options */}
      <Box>
        <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 3 }}>
          Share Your Job
        </Typography>
        
        {/* All Share Options in One Row */}
        <Stack direction="row" spacing={2}>
          {/* Email */}
          <Box
            onClick={handleShareEmail}
            sx={{
              flex: 1,
              p: 3,
              borderRadius: '12px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E5E7EB',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#2e2d7e',
                backgroundColor: '#F0F2F5',
              },
            }}
          >
            <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>📧</Typography>
            <Typography level="body-sm" sx={{ fontWeight: 600, color: '#1A1B1D' }}>
              Email
            </Typography>
          </Box>

          {/* LinkedIn */}
          <Box
            onClick={() => handleShareSocial('linkedin')}
            sx={{
              flex: 1,
              p: 3,
              borderRadius: '12px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E5E7EB',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#0077B5',
                backgroundColor: '#F0F8FF',
              },
            }}
          >
            <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>💼</Typography>
            <Typography level="body-sm" sx={{ fontWeight: 600, color: '#1A1B1D' }}>
              LinkedIn
            </Typography>
          </Box>
          
          {/* Twitter */}
          <Box
            onClick={() => handleShareSocial('twitter')}
            sx={{
              flex: 1,
              p: 3,
              borderRadius: '12px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E5E7EB',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#1DA1F2',
                backgroundColor: '#F0F8FF',
              },
            }}
          >
            <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>🐦</Typography>
            <Typography level="body-sm" sx={{ fontWeight: 600, color: '#1A1B1D' }}>
              Twitter
            </Typography>
          </Box>
          
          {/* Facebook */}
          <Box
            onClick={() => handleShareSocial('facebook')}
            sx={{
              flex: 1,
              p: 3,
              borderRadius: '12px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E5E7EB',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#1877F2',
                backgroundColor: '#F0F8FF',
              },
            }}
          >
            <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>📘</Typography>
            <Typography level="body-sm" sx={{ fontWeight: 600, color: '#1A1B1D' }}>
              Facebook
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Next Steps */}
      <Box sx={{ 
        p: 3, 
        borderRadius: '12px', 
        border: '2px solid #E3F2FD',
        bgcolor: 'linear-gradient(135deg, #2E2D7E 0%, #4A4A9E 100%)',
        background: 'linear-gradient(135deg, #2E2D7E 0%, #4A4A9E 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 70% 30%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            level="h4" 
            sx={{ 
              fontWeight: 700, 
              color: 'white', 
              mb: 2,
              fontSize: '1.5rem',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            WHAT'S NEXT?
          </Typography>
          <Typography 
            level="body-lg" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              mb: 3,
              lineHeight: 1.5
            }}
          >
            Get more candidates, add a video to your job post.
          </Typography>
          <Button
            onClick={() => {
              // Use actual slugs from workflow data first, then fallback to API data
              const employerSlug = data.employerSlug || employerData?.slug;
              const positionSlug = data.positionSlug;
              
              if (!employerSlug || !positionSlug) {
                console.error('Missing employer or position slug for edit URL:', { employerSlug, positionSlug });
                return;
              }
              
              const editUrl = `/hiring/${employerSlug}/${positionSlug}/edit`;
              window.open(editUrl, '_blank', 'noopener,noreferrer');
            }}
            sx={{
              bgcolor: 'white',
              color: '#2E2D7E',
              px: 3,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              '&:hover': {
                bgcolor: '#f8f9fa',
                color: '#2E2D7E',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 24px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            Edit Post
          </Button>
        </Box>
      </Box>
    </Stack>
  );
};

export default SharePositionStep;
